import React, { useState,useEffect } from "react";
import {
  Card,
  CardBody,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  Button,
  Input,
  Container,
} from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLocation, useHistory } from "react-router-dom";
import CustomMonthPicker from "common/CustomMonthPicker";
import { useDispatch, useSelector } from "react-redux";
import Loader from "common/pageLoader";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { punchlistGetTemplate, punchlistImport, punchlistSave, resetAll } from "store/actions";
import Swal from "sweetalert2";
import getAccessRights from "components/Common/getAccessRights";
import useButtonAccess from "components/Common/useButtonAccess";

const BiometricImportPunchList = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
      const rights = getAccessRights(location.pathname, history);
      setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    importedData,
    template,
    loading,
    error,
    success,
  } = useSelector((state) => ({
    importedData: state.AttendanceBiometricReducer?.importedData,
    template: state.AttendanceBiometricReducer?.template,

    loading: state.AttendanceBiometricReducer?.loading,
    error: state.AttendanceBiometricReducer?.error ,
    success: state.AttendanceBiometricReducer?.success ,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    dispatch(resetAll())
  }, [dispatch]);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    dispatch(resetAll())
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      month: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      file: null,
    },
    validationSchema: Yup.object({
      month: Yup.string().nullable().required("Month is required"),
      file: Yup.mixed()
        .required("Please upload an Excel file")
        .test(
          "fileFormat",
          "Only Excel files are allowed (.xls, .xlsx)",
          (value) =>
            value &&
            [
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ].includes(value.type)
        ),
    }),
    onSubmit: (values) => {
      // console.log("Form Submitted:", values);
      const formData = new FormData();
      formData.append("excelFile", values.file);
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(punchlistImport(formData))
    },
  });

  useEffect(() => {
      if(success){
        dispatch(resetAll())
        formik.resetForm()
      }
  }, [authDetails,success,dispatch]);

  const handleGetTemplate = () => {
    // Check if month is selected
    if (!formik.values.month) {
      Swal.fire("Warning", "Please select a month before downloading the template!", "warning");
      return;
    }

    // Format date as YYYY-MM-01
    const date = new Date(formik.values.month);
    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;

    // Prepare FormData
    const formData = new FormData();
    formData.append("salary_month", formattedDate);
    formData.append("unit_id", authDetails?.unit_id);
    formData.append("create_by", authDetails?.user_id);

    // Dispatch Redux action
    dispatch(punchlistGetTemplate(formData));
  };

  const handleSave = () => {
    if (!importedData || importedData.length === 0) {
      Swal.fire("Warning", "No data to save!", "warning");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to save the imported punch list?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          lst_employee_PunchList: importedData
            .filter((item) => item.in_time && item.out_time)
            .map((item) => ({
              emp_no: item.emp_no,
              punch_date: item.punch_date,
              in_time: item.in_time,
              out_time: item.out_time,
              Remarks: item.Remarks || "Manual",
            })),
        };

        const formData = new FormData();
        formData.append("unit_id", authDetails?.unit_id);
        formData.append("create_by", authDetails?.user_id);
        formData.append("lst_employee_PunchList", JSON.stringify(payload));

        dispatch(punchlistSave(formData));
      }
    });
  };

  const columns = [
    { Header: "S.No", Cell: ({ row }) => importedData.length - row.index, },
    { Header: "Emp No", accessor: "emp_no" },
    { Header: "Employee Name", accessor: "emp_name" },
    { Header: "Punch Date", accessor: "punch_date" },
    { Header: "Shift Timings", accessor: "shift_timings" },
    { Header: "In Time", accessor: "in_time" },
    { Header: "Out Time", accessor: "out_time" },
  ];


  return (
    <React.Fragment>
      <div className="page-content">
        <Loader loading={loading}/>
        <Container fluid>
          <Breadcrumbs title="Biometric" breadcrumbItem="Import Punchlist" />

          <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row>
                  {/* Month Picker */}
                  <Col md={3} sm={12} className="mb-3">
                    <FormGroup>
                      <Label for="month">
                        Month <span className="text-danger">*</span>
                      </Label>
                      <CustomMonthPicker
                        name="month"
                        value={formik.values.month}
                        onChange={(date) => formik.setFieldValue("month", date)}
                        onBlur={formik.handleBlur}
                        disableFuture={true}
                        className={`form-control ${
                          formik.touched.month && formik.errors.month
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      {formik.touched.month && formik.errors.month && (
                        <div className="text-danger">{formik.errors.month}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* File Upload */}
                  <Col md={3} sm={12} className="mb-3">
                    <FormGroup>
                      <Label for="file">
                        File <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="file"
                        name="file"
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={(event) =>
                          formik.setFieldValue("file", event.currentTarget.files[0])
                        }
                        onBlur={formik.handleBlur}
                        className={`form-control ${
                          formik.touched.file && formik.errors.file
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      {formik.touched.file && formik.errors.file && (
                        <div className="text-danger">{formik.errors.file}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Buttons */}
                  {(canRead && canCreate) && (
                      <Col
                        md={6}
                        sm={12}
                        className="d-flex mob-center align-items-center justify-content-end"
                      >
                        {/* Get Template */}
                        <Button
                          type="button"
                          color="primary"
                          className="btn button-export me-2"
                          onClick={handleGetTemplate}
                        >
                          <i className="fas fa-download me-1" /> Get Template
                        </Button>

                        {/* Import (acts as form submit) */}
                        <Button
                          type="submit"
                          color="info"
                          className="btn button-search me-2"
                        >
                          <i className="fas fa-file-import me-1" /> Import
                        </Button>

                        {/* Show Save only if imported data exists */}
                        {importedData && importedData.length > 0 && (
                          <Button
                            type="button"
                            color="success"
                            className="btn button-save"
                            onClick={handleSave}
                          >
                            <i className="fas fa-save me-1" /> Save
                          </Button>
                        )}
                      </Col>
                  )}
                  
                </Row>
              </Form>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <TableContainer columns={columns} data={importedData && importedData.length > 0 ? importedData : []} isGlobalFilter={true} customPageSize={10} />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BiometricImportPunchList;
